import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import BaseModal from "../../../components/ui/BaseModal";
import BaseCard from "../../../components/ui/BaseCard";
import BaseButton from "../../../components/ui/BaseButton";
import { showToast } from "../../../store/toast/toastSlice";
import { withdrawCustomerCounterOffer } from "../../../services/api/offers";
import { fetchWorkOrderPaymetDetails } from "../../../services/api/workOrder";
import { CurrencySymbol } from "../../../utils/enum";
import { formatExpenseLabel } from "../../../utils/helpers";
import { getTimeZoneAbbreviation } from "../../../utils/getTimeZoneAbbreviation";
import {
  RATE_FIELDS,
  isRateShown,
  estimateLabour,
  fmt,
  toCustomerRateField,
  toNum,
} from "../CustomerCounterOfferModal/customerRates";

// The counter offer the client sent the customer, still awaiting their answer, laid
// out like the engineer's WithdrawCounterOfferModal: the rates as sent (struck
// through where the proposal differs), the proposed expenses and the estimated
// payment. From here the client takes it back; afterwards they can send a new one
// (editing has its own button on the action bar).
const WithdrawCustomerCounterOfferModal = ({ open, close, counterOffer }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  const paymentType = workOrderDetail?.paymentType;
  const fields = RATE_FIELDS[paymentType] || [];
  const currency = CurrencySymbol[counterOffer?.customerCurrency || counterOffer?.currency] || "";

  const { isPending: isWithdrawing, mutateAsync: withdrawMutation } = useMutation({
    mutationFn: withdrawCustomerCounterOffer,
    onSuccess: () => {
      dispatch(showToast({ message: "Counter Offer Withdrawn", type: "success" }));
      queryClient.invalidateQueries({ queryKey: ["customerCounterOffer"] });
      close();
    },
    onError: (error) =>
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
        }),
      ),
  });

  // The current payment, for the before/after estimate.
  const { data: workOrderPaymentData } = useQuery({
    queryKey: ["workOrderPayment", workOrderDetail?.id],
    queryFn: () => fetchWorkOrderPaymetDetails(workOrderDetail.id),
    enabled: !!workOrderDetail?.id,
  });

  // The offer stores the work order's rates when it was sent (unprefixed) and the
  // proposed ones (customer*).
  const sentRates = Object.fromEntries(fields.map(({ name }) => [name, counterOffer?.[name]]));
  const proposedRates = Object.fromEntries(
    fields.map(({ name }) => [
      name,
      counterOffer?.[toCustomerRateField(name)] ?? counterOffer?.[name],
    ]),
  );
  const expenses = counterOffer?.expenses || [];
  const expensesTotal = expenses.reduce((sum, expense) => sum + (toNum(expense.amount) || 0), 0);
  const currentTotal = toNum(workOrderPaymentData?.data?.clientRates?.totalWorkOrderCost);
  const newTotal =
    currentTotal != null
      ? currentTotal -
        estimateLabour(paymentType, sentRates) +
        estimateLabour(paymentType, proposedRates) +
        expensesTotal
      : null;

  const renderRateValue = (isAmount, value) =>
    isAmount ? `${currency} ${fmt(value)}` : `${value ?? 0} hrs`;

  return (
    <BaseModal open={open} close={close}>
      <div className="container bg-light xl:px-10">
        <h5 className="text-dark border-bottom mb-3">Withdraw Counter Offer</h5>

        <BaseCard className="p-4 mt-6" elevation={0}>
          <div className="flex justify-between border-bottom mb-2">
            <h6 className="text-dark">Schedule</h6>
            <div className="flex items-center bg-[#F2FAFF] py-1 px-4 rounded-md mb-2">
              <i className="fa-solid fa-circle-exclamation text-primary"></i>
              <p className=" ml-3 font-normal">Fixed Start</p>
            </div>
          </div>
          <div className="flex justify-between">
            <p>Start at a specific date and time</p>
            {workOrderDetail?.orderStartDate ? (
              <p>
                {dayjs.utc(workOrderDetail.orderStartDate).format("dddd, MM/DD/YYYY")} at{" "}
                {dayjs.utc(workOrderDetail.orderStartTime).format("h:mm A")}
                <br />({getTimeZoneAbbreviation(workOrderDetail.timeZone)})
              </p>
            ) : (
              <p>-</p>
            )}
          </div>

          <div>
            <h6>Labour</h6>
            {fields
              .filter((field) =>
                isRateShown(field, sentRates[field.name], proposedRates[field.name]),
              )
              .map(({ name, label, isAmount }, index) => {
                const isChanged = toNum(proposedRates[name]) !== toNum(sentRates[name]);
                return (
                  <div key={name} className={`flex items-center mb-2 ${index === 0 ? "mt-4" : ""}`}>
                    <p className="mr-5">{label}</p>
                    <div className="border-t border-dotted border-gray-500 flex-grow"></div>
                    <p className={`ml-5 font-normal ${isChanged ? "line-through" : ""}`}>
                      {renderRateValue(isAmount, sentRates[name])}
                    </p>
                    {isChanged && (
                      <p className="ml-3 font-normal">
                        {renderRateValue(isAmount, proposedRates[name])}
                      </p>
                    )}
                  </div>
                );
              })}
          </div>

          {expenses.length > 0 && (
            <div className="mt-6">
              <h6>Expenses</h6>
              {expenses.map((expense) => (
                <div className="flex items-center mt-2 mb-3" key={expense.id}>
                  <p className="mr-5">{formatExpenseLabel(expense)}</p>
                  <div className="border-t border-dotted border-gray-500 flex-grow"></div>
                  <div className="flex items-center bg-light py-1 px-3 rounded-md ml-5">
                    <i className="fa-solid fa-xmark"></i>
                    <p className="ml-2 font-normal">
                      {currency} {fmt(expense.amount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6">
            <div className="flex justify-between bg-primary px-4 py-1 rounded-md">
              <h6 className="!text-white">Estimated Payment</h6>
              <div className="flex">
                {newTotal != null && newTotal !== currentTotal && (
                  <h6 className="!text-white line-through mr-6">
                    {currency} {fmt(currentTotal)}
                  </h6>
                )}
                {/* Until the current payment has loaded there is no total to show. */}
                <h6 className="!text-white">
                  {currentTotal != null
                    ? `${currency} ${fmt(newTotal ?? currentTotal)}`
                    : "Calculating..."}
                </h6>
              </div>
            </div>

            <p className="mt-3 text-primary xl:pr-28 text-justify">
              <span className="font-normal">Please note:</span> This is the minimum payable amount;
              however, the final payment will be determined upon job completion based on the actual
              time worked.
            </p>
          </div>
        </BaseCard>
      </div>

      <div
        className="flex flex-wrap justify-center gap-4 py-8"
        style={{
          boxShadow: "0px 0px 14px 5px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          fullWidth={false}
          className="py-[4px]"
          variant="outlined"
          color="primary"
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>
        <BaseButton
          fullWidth={false}
          className="py-[5px]"
          color="primary"
          loaderSize="26px"
          isLoading={isWithdrawing}
          disabled={isWithdrawing}
          // onError has already shown the toast; don't let the rejection escape too.
          onClick={() => withdrawMutation(counterOffer.id).catch(() => {})}
        >
          Withdraw
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default WithdrawCustomerCounterOfferModal;
